import {
  ALARM_CATEGORIES,
  ALARM_CATEGORY_LABELS,
  ALARM_SEVERITY_LABELS,
  type AlarmRuleView,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  effectiveSeverityText,
  formatThreshold,
  overrideSource,
  thresholdLabel,
} from "../../../features/alarms/alarm-values.js";
import {
  useAlarmPermissions,
  useAlarmRules,
  useRefreshAlarms,
  useStationOptions,
} from "../../../features/alarms/queries.js";
import { RuleDialog } from "../../../features/alarms/rule-dialog.js";
import { AlarmsPage } from "../../../features/alarms/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({ stationId: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/_app/alarmlar/kurallar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: AlarmRulesRoute,
});

/**
 * Alarm kural ayarları (Prompt 20 web, `GET /alarm-rules`): kategori gruplarında kural etiketi + açıklama, varsayılan ve
 * etkin önem, etkin / kapalı, eşikler, yükseltme süresi ve geçersiz kılma KAYNAĞI (katalog / kiracı / istasyon). Düzey
 * seçici: kiracı geneli ya da bir istasyon (tek istasyonlu kiracıda yalnızca kiracı geneli). Düzenleme ve "varsayılana
 * dön" `alarms.rules.manage` ister; sunucunun `canEditTenant` / `canEditStation`'ı düğmeleri belirler (yoksa salt okuma).
 */
function AlarmRulesRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <AlarmsPage
      title={t("menu.alarmRules")}
      description={t("alarms.rules.description")}
      requirement="rules"
      breadcrumb={[
        {
          label: t("menu.alarms"),
          link: (children, className) => (
            <Link to="/alarmlar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: t("menu.alarmRules") },
      ]}
    >
      <AlarmRulesBody />
    </AlarmsPage>
  );
}

function AlarmRulesBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshAlarms();
  const permissions = useAlarmPermissions();
  const shape = useTenantShape();
  const search = Route.useSearch();
  const stationId = shape.isSingleStation ? null : (search.stationId ?? null);
  const stations = useStationOptions(!shape.isSingleStation);
  const list = useAlarmRules(stationId);
  const [editing, setEditing] = useState<AlarmRuleView | null>(null);
  const [resetting, setResetting] = useState<AlarmRuleView | null>(null);
  const station = (stations.data ?? []).find((item) => item.id === stationId) ?? null;
  const stationLabel = station ? `${station.code} · ${station.name}` : null;
  const canEdit = list.data ? (stationId ? list.data.canEditStation : list.data.canEditTenant) : false;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-4 p-4">
        {!shape.isSingleStation ? (
          <FormField label={t("alarms.rules.level")} className="w-full sm:w-72">
            {(control) => (
              <Select
                {...control}
                value={stationId ?? ""}
                onChange={(event) =>
                  void navigate({
                    to: ".",
                    search: (event.target.value ? { stationId: event.target.value } : {}) as never,
                    replace: true,
                  })
                }
              >
                <option value="">{t("alarms.rules.tenantLevel")}</option>
                {(stations.data ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} · {item.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <p className="max-w-2xl text-sm text-muted-foreground">
          {stationId ? t("alarms.rules.stationHint") : t("alarms.rules.tenantHint")}
        </p>
      </Card>
      {list.data && !canEdit ? (
        <Alert tone="info">
          {!permissions.canManageRules
            ? t("alarms.rules.readOnly")
            : stationId
              ? t("alarms.rules.cannotEditStation")
              : t("alarms.rules.cannotEditTenant")}
        </Alert>
      ) : null}
      {list.error ? (
        <Alert tone="danger">{errorMessage(list.error)}</Alert>
      ) : list.isPending ? (
        <SkeletonRows rows={8} />
      ) : (
        ALARM_CATEGORIES.map((category) => {
          const rules = list.data.rules.filter((rule) => rule.category === category);
          if (rules.length === 0) return null;
          const caption = ALARM_CATEGORY_LABELS[category];
          return (
            <Card key={category} className="grid gap-3 p-4">
              <h2 className="text-base font-semibold">{caption}</h2>
              <div
                className="relative overflow-x-auto rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                tabIndex={0}
                role="region"
                aria-label={t("alarms.rules.tableLabel", { category: caption })}
              >
                {/* Sabit sütun genişlikleri: kategori tabloları alt alta aynı hizada okunur. */}
                <table className="w-full min-w-[60rem] table-fixed border-collapse text-sm">
                  <caption className="sr-only">{t("alarms.rules.tableLabel", { category: caption })}</caption>
                  <colgroup>
                    <col className="w-[26%]" />
                    <col className="w-[9%]" />
                    <col className="w-[11%]" />
                    <col className="w-[7%]" />
                    <col className="w-[17%]" />
                    <col className="w-[8%]" />
                    <col className="w-[9%]" />
                    <col className="w-[13%]" />
                  </colgroup>
                  <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.fields.rule")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.rules.defaultSeverity")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.rules.effectiveSeverity")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.fields.status")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.rules.thresholds")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.rules.escalation")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        {t("alarms.rules.source")}
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        <span className="sr-only">{t("common.actions")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => {
                      const source = overrideSource(rule);
                      const ownOverride = stationId ? rule.stationOverride : rule.tenantOverride;
                      const thresholds = Object.entries(rule.effective.thresholds);
                      return (
                        <tr key={rule.code} className="border-t align-top">
                          <th scope="row" className="px-3 py-3 text-left font-normal">
                            <span className="grid gap-0.5">
                              <span className="font-medium">{rule.label}</span>
                              <span className="text-xs text-muted-foreground">{rule.description}</span>
                            </span>
                          </th>
                          <td className="whitespace-nowrap px-3 py-3">
                            {ALARM_SEVERITY_LABELS[rule.defaults.severity]}
                          </td>
                          <td className="px-3 py-3">{effectiveSeverityText(rule, t)}</td>
                          <td className="px-3 py-3">
                            <StatusBadge tone={rule.effective.enabled ? "success" : "neutral"}>
                              {rule.effective.enabled ? t("alarms.rules.on") : t("alarms.rules.off")}
                            </StatusBadge>
                          </td>
                          <td className="px-3 py-3">
                            {thresholds.length === 0 ? (
                              <span className="text-muted-foreground">{t("alarms.rules.moduleShort")}</span>
                            ) : (
                              <ul className="grid gap-0.5">
                                {thresholds.map(([key, value]) => (
                                  <li key={key}>
                                    {thresholdLabel(key, rule.thresholdLimits[key]?.label)}:{" "}
                                    <span className="tabular font-medium">{formatThreshold(key, value)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3">
                            {rule.effective.escalationMinutes !== null
                              ? t("alarms.rules.minutes", { count: rule.effective.escalationMinutes })
                              : "—"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3">
                            <StatusBadge tone={source === "CATALOG" ? "neutral" : "info"}>
                              {t(`alarms.rules.sourceLabel.${source}`)}
                            </StatusBadge>
                          </td>
                          <td className="px-3 py-3 text-right">
                            {canEdit ? (
                              <span className="flex flex-wrap justify-end gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEditing(rule)}
                                  aria-label={t("alarms.rules.editLabel", { rule: rule.label })}
                                >
                                  {t("common.edit")}
                                </Button>
                                {ownOverride ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setResetting(rule)}
                                    aria-label={t("alarms.rules.resetLabel", { rule: rule.label })}
                                  >
                                    {t("alarms.rules.reset")}
                                  </Button>
                                ) : null}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })
      )}
      {editing ? (
        <RuleDialog
          open
          onOpenChange={(open) => (open ? undefined : setEditing(null))}
          rule={list.data?.rules.find((rule) => rule.code === editing.code) ?? editing}
          stationId={stationId}
          stationLabel={stationLabel}
        />
      ) : null}
      <ConfirmDialog
        open={resetting !== null}
        onOpenChange={(open) => (open ? undefined : setResetting(null))}
        title={t("alarms.rules.resetTitle", { rule: resetting?.label ?? "" })}
        description={
          stationLabel
            ? t("alarms.rules.resetStationDescription", { station: stationLabel })
            : t("alarms.rules.resetTenantDescription")
        }
        confirmLabel={t("alarms.rules.reset")}
        onConfirm={async () => {
          if (!resetting) return;
          try {
            await api.DELETE("/alarm-rules/{code}", {
              params: {
                path: { code: resetting.code },
                query: (stationId ? { stationId } : {}) as never,
              },
            });
            await refresh();
            toast({ tone: "success", title: t("alarms.rules.resetDone", { rule: resetting.label }) });
          } catch (caught) {
            toast({ tone: "danger", title: errorMessage(caught) });
            throw caught;
          }
        }}
      />
    </>
  );
}
